import { all, call, takeEvery, put, delay } from 'typed-redux-saga/macro';
import * as Sentry from '@sentry/react';
import { FirebaseError } from 'firebase/app';
import axios from 'axios';
import type { SignInResult } from '@capacitor-firebase/authentication';

import callApi, { METHODS, PATHS } from 'api';
import { ERROR_MESSAGES } from 'utils/constants';
import { authClient } from 'utils/capawesomeAuth';
import { fetchUserInfo, fetchUserUsage } from 'features/Auth/actions';
import { setInitialAuthDone, setFirebaseAuthenticated } from 'features/Auth/slice';

import { SIGN_UP_WITH_APPLE, SIGN_UP_WITH_GOOGLE, SUBMIT_REGISTRATION_FORM } from './actionTypes';
import { REGISTRATION_ERROR_MESSAGES } from './constants';
import { IOAuthRegistrationSubmitProps, IRegistrationSubmitProps } from './types';

type OAuthProvider = 'google' | 'apple';

interface IRegistrationFormSubmitSaga {
  type: typeof SUBMIT_REGISTRATION_FORM;
  payload: IRegistrationSubmitProps;
}

interface IOAuthRegistrationSaga {
  type: typeof SIGN_UP_WITH_GOOGLE | typeof SIGN_UP_WITH_APPLE;
  payload: IOAuthRegistrationSubmitProps;
}

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 500;

interface IRegistrationQueryParams {
  name: string | null;
  email: string | null;
  web2wave_user_id: string | null;
}

const getRegistrationQueryParams = (): IRegistrationQueryParams => {
  if (typeof window === 'undefined') {
    return {
      name: null,
      email: null,
      web2wave_user_id: null,
    };
  }

  const params = new URLSearchParams(window.location.search);
  const rawName = params.get('name');
  const rawEmail = params.get('email');
  const rawWeb2waveId = params.get('web2wave_user_id');

  return {
    name: rawName ?? null,
    email: rawEmail ? rawEmail.replace(/ /g, '+') : null,
    web2wave_user_id: rawWeb2waveId ?? null,
  };
};

const isTransientError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) return false;

  const status = error.response?.status;
  if (status) {
    if (status >= 500 || status === 429) return true;
    if (status >= 400) return false;
  }

  const code = error.code?.toUpperCase();
  if (code && ['ECONNABORTED', 'ETIMEDOUT', 'ECONNRESET', 'ERR_NETWORK'].includes(code)) {
    return true;
  }

  const message = error.message?.toLowerCase();
  if (message && (message.includes('network error') || message.includes('timeout'))) {
    return true;
  }

  return Boolean(error.request && !error.response);
};

interface IBackendRegistrationPayload {
  name: string | null;
  email: string;
  web2wave_id?: string;
}

const buildBackendRegistrationPayload = (
  name: string | null,
  email: string,
  web2wave_user_id?: string
): IBackendRegistrationPayload => {
  const payload: IBackendRegistrationPayload = {
    name: typeof name === 'string' ? name.trim() || null : null,
    email: email.trim(),
  };

  if (web2wave_user_id?.trim()) {
    payload.web2wave_id = web2wave_user_id.trim();
  }

  return payload;
};

function* registerUserWithBackend(payload: IBackendRegistrationPayload) {
  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    try {
      yield* call(callApi, {
        method: METHODS.POST,
        mainPath: PATHS.REGISTER,
        data: payload,
        authorized: true,
      });

      return;
    } catch (error: unknown) {
      if (!isTransientError(error) || attempt === MAX_RETRIES) {
        throw error;
      }

      yield* delay(RETRY_DELAY_MS * attempt);
    }
  }
}

const getOAuthErrorMessage = (error: FirebaseError, provider: OAuthProvider): string => {
  const providerName = provider === 'google' ? 'Google' : 'Apple';

  switch (error.code) {
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return `${providerName} sign-in was canceled. Please try again.`;
    case 'auth/popup-blocked':
      return 'Please enable pop-ups in your browser to continue.';
    case 'auth/account-exists-with-different-credential':
    case 'auth/credential-already-in-use':
      return REGISTRATION_ERROR_MESSAGES.EMAIL_ALREADY_IN_USE;
    case 'auth/operation-not-allowed':
      return `${providerName} sign-in is currently unavailable. Please try another method.`;
    case 'auth/user-disabled':
      return `This ${providerName} account has been disabled.`;
    case 'auth/network-request-failed':
      return `Network error while contacting ${providerName}. Please try again.`;
    default:
      Sentry.captureException(error);
      return ERROR_MESSAGES.SOMETHING_WENT_WRONG;
  }
};

function* registrationFormSubmitSaga({
  payload: { name, email, password, actions, web2wave_user_id },
}: IRegistrationFormSubmitSaga) {
  let firebaseAccountCreated = false;

  try {
    actions.setSubmitting(true);

    const result: SignInResult = yield* call(
      [authClient, authClient.createUserWithEmailAndPassword],
      {
        email,
        password,
      }
    );

    firebaseAccountCreated = !!result.user;

    const registrationPayload = buildBackendRegistrationPayload(name, email, web2wave_user_id);
    yield* call(registerUserWithBackend, registrationPayload);

    yield* put(setFirebaseAuthenticated(true));
    yield* put(fetchUserInfo());
    yield* put(fetchUserUsage());
    yield* put(setInitialAuthDone(true));
  } catch (error: unknown) {
    let errorMessage = ERROR_MESSAGES.SOMETHING_WENT_WRONG;

    if (!(error instanceof FirebaseError) && firebaseAccountCreated) {
      yield* call([authClient, authClient.deleteUser]);
    }

    if (error instanceof FirebaseError) {
      switch (error.code) {
        case 'auth/email-already-in-use':
          errorMessage = REGISTRATION_ERROR_MESSAGES.EMAIL_ALREADY_IN_USE;
          break;
        case 'auth/invalid-email':
          errorMessage = REGISTRATION_ERROR_MESSAGES.INVALID_EMAIL;
          break;
        case 'auth/weak-password':
          errorMessage = REGISTRATION_ERROR_MESSAGES.WEAK_PASSWORD;
          break;
        default:
          errorMessage = ERROR_MESSAGES.SOMETHING_WENT_WRONG;
          Sentry.captureException({ error, email });
      }
    } else {
      errorMessage = 'Registration failed. Please try again.';
      Sentry.captureException({
        error,
        email,
        context: 'Backend registration failed after retries',
      });
    }

    actions.setFieldError('form', errorMessage);
  } finally {
    actions.setSubmitting(false);
  }
}

function* oauthSignUpSaga(
  provider: OAuthProvider,
  { payload: { actions, web2wave_user_id } }: IOAuthRegistrationSaga
) {
  const providerName = provider === 'google' ? 'Google' : 'Apple';
  let signInResult: SignInResult | null = null;
  let userEmail: string | null = null;
  let isNewUser = false;

  try {
    actions.setSubmitting(true);

    if (provider === 'google') {
      signInResult = yield* call([authClient, authClient.signInWithGoogle], {
        useCredentialManager: false,
      });
    } else {
      signInResult = yield* call([authClient, authClient.signInWithApple]);
    }

    const { user, additionalUserInfo } = signInResult;
    const { email: urlEmail, name: urlName } = getRegistrationQueryParams();
    const emailFromQuery = urlEmail?.trim() || null;
    const providerEmail = user?.email?.trim() || null;
    userEmail = emailFromQuery ?? providerEmail;

    if (!userEmail) {
      throw new Error(`${providerName} account did not provide an email address.`);
    }

    isNewUser = additionalUserInfo?.isNewUser ?? false;
    const nameFromQuery = urlName;
    const providerDisplayName = user?.displayName?.trim() || null;
    const normalizedName = nameFromQuery ?? providerDisplayName ?? null;

    if (isNewUser) {
      const registrationPayload = buildBackendRegistrationPayload(
        normalizedName,
        userEmail,
        web2wave_user_id
      );
      yield* call(registerUserWithBackend, registrationPayload);
    }

    yield* put(setFirebaseAuthenticated(true));
    yield* put(fetchUserInfo());
    yield* put(fetchUserUsage());
    yield* put(setInitialAuthDone(true));
  } catch (error: unknown) {
    if (isNewUser && signInResult?.user) {
      yield* call([authClient, authClient.deleteUser]);
    }

    let errorMessage = `${providerName} sign-in failed. Please try again.`;

    if (error instanceof FirebaseError) {
      errorMessage = getOAuthErrorMessage(error, provider);
    } else {
      Sentry.captureException({
        error,
        email: userEmail,
        context: `${providerName} registration flow failed`,
      });

      if (axios.isAxiosError(error)) {
        errorMessage = 'Registration failed. Please try again.';
      } else if (error instanceof Error && error.message.includes('email address')) {
        errorMessage = `We could not retrieve your ${providerName} email. Please use another method.`;
      }
    }

    actions.setFieldError('form', errorMessage);
  } finally {
    actions.setSubmitting(false);
  }
}

function* googleSignUpSaga(action: IOAuthRegistrationSaga) {
  yield* oauthSignUpSaga('google', action);
}

function* appleSignUpSaga(action: IOAuthRegistrationSaga) {
  yield* oauthSignUpSaga('apple', action);
}

export function* watchRegistrationForm() {
  yield* all([
    takeEvery(SUBMIT_REGISTRATION_FORM, registrationFormSubmitSaga),
    takeEvery(SIGN_UP_WITH_GOOGLE, googleSignUpSaga),
    takeEvery(SIGN_UP_WITH_APPLE, appleSignUpSaga),
  ]);
}
